import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef, googleAuthApiRef } from '@backstage/core-plugin-api';
import {
  Header,
  Page,
  Content,
  InfoCard,
} from '@backstage/core-components';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  TablePagination,
  makeStyles,
} from '@material-ui/core';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import Editor, { loader } from '@monaco-editor/react';
import { CreatePipelineDialog } from '@internal/backstage-plugin-sql-pipeline';

/**
 * monaco 를 모듈 최상위에서 import 하면 안 된다.
 *
 * `monaco-editor` 의 package main 은 AMD 번들(min/vs/editor/editor.main.js)이라
 * 평가되는 순간 `define()` 을 호출한다. jest 환경에는 `define` 이 없으므로
 * 이 모듈을 import 하는 것만으로 테스트 스위트가 죽었고, App.tsx 가 이 파일을
 * import 하기 때문에 **앱 테스트 전체가 실행 불가**였다. 또한 이 모듈을
 * import 하기만 해도 수 MB 번들이 끌려온다.
 *
 * 따라서 컴포넌트가 실제로 마운트될 때 1회만 동적 import 한다. `loader.config`
 * 는 `<Editor>` 가 로더를 시작하기 전에 적용되어야 하므로(늦으면 CDN 에서
 * 내려받으려 한다) 설정 완료 전에는 에디터를 렌더하지 않는다.
 */
let monacoSetup: Promise<void> | undefined;
const configureMonacoOnce = () => {
  if (!monacoSetup) {
    monacoSetup = import('monaco-editor').then(monaco => {
      loader.config({ monaco });
    });
  }
  return monacoSetup;
};

const useStyles = makeStyles(theme => ({
  editorWrapper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  resultInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    maxHeight: 520,
    overflow: 'auto',
    '& table': {
      tableLayout: 'auto',
    },
  },
  headerCell: {
    fontWeight: 700,
    backgroundColor: theme.palette.background.default,
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
    whiteSpace: 'nowrap',
    minWidth: 180,
    fontSize: '0.8rem',
  },
  dataCell: {
    fontSize: '0.8rem',
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
    minWidth: 180,
  },
  nullValue: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
  errorBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? '#2d1f1f' : '#fff3f3',
    borderRadius: 4,
    border: `1px solid ${theme.palette.error.light}`,
  },
}));

interface QueryResult {
  columns: string[];
  rows: Record<string, any>[];
  totalRows: number;
  truncated: boolean;
}

export const SqlConsoleContent = () => {
  const classes = useStyles();
  const [createOpen, setCreateOpen] = useState(false);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const googleAuthApi = useApi(googleAuthApiRef);

  const [sql, setSql] = useState('SELECT 1 AS test_column');
  const handleRunQueryRef = useRef<() => void>(() => {});
  const [result, setResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [editorReady, setEditorReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    configureMonacoOnce().then(() => {
      if (!cancelled) setEditorReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const getBaseUrl = useCallback(async () => {
    return await discoveryApi.getBaseUrl('bigquery-api');
  }, [discoveryApi]);

  const getGoogleToken = useCallback(async () => {
    return await googleAuthApi.getAccessToken(
      'https://www.googleapis.com/auth/bigquery.readonly',
    );
  }, [googleAuthApi]);

  const handleRunQuery = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setPage(0);

    try {
      const [baseUrl, googleToken] = await Promise.all([getBaseUrl(), getGoogleToken()]);
      const response = await fetchApi.fetch(`${baseUrl}/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Google-Token': googleToken,
        },
        body: JSON.stringify({ sql }),
      });

      const data = await response.json();
      if (!response.ok) {
        setError(data.error ?? '쿼리 실행에 실패했습니다.');
      } else {
        setResult(data);
      }
    } catch (e: any) {
      setError(e.message ?? '서버 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [sql, getBaseUrl, getGoogleToken, fetchApi]);

  handleRunQueryRef.current = handleRunQuery;


  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // 이전에는 `formData` 쿼리스트링으로 scaffolder 에 SQL 을 넘겼는데, 템플릿에
  // 그 파라미터 선언이 없으면 scaffolder 가 unknown formData 를 조용히 떨어뜨렸다.
  // 다이얼로그에 prop 으로 넘기면 그 계약이 타입으로 보인다.
  const handleCreatePipeline = () => setCreateOpen(true);

  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined) return 'NULL';
    if (typeof value === 'object') {
      // BigQuery SDK wraps TIMESTAMP, DATE, NUMERIC 등을 { value: "..." } 로 반환
      if ('value' in value && Object.keys(value).length === 1) {
        return String(value.value);
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  return (
    <>
        <Box mb={2} p={2} borderRadius={4} style={{ backgroundColor: '#433200', border: '1px solid #665200' }}>
          <Typography variant="body2" style={{ color: '#ffe082' }}>
            SQL Console은 파이프라인에 등록할 쿼리의 결과를 간단히 확인하기 위한 용도입니다.
            최대 <strong style={{ color: '#ffca28' }}>1,000건</strong>까지만 조회 가능하며, 대량 데이터 조회가 필요한 경우 <strong style={{ color: '#ffca28' }}>BigQuery Console</strong>을 이용해주세요.
          </Typography>
        </Box>
        <InfoCard
          title={
            <Box display="flex" alignItems="center" gridGap={12}>
              <span>SQL Editor</span>
              <Button
                size="small"
                variant="contained"
                startIcon={<OpenInNewIcon />}
                href="https://console.cloud.google.com/bigquery"
                target="_blank"
                rel="noopener noreferrer"
                style={{ backgroundColor: '#1e88e5', color: '#fff' }}
              >
                BigQuery Console
              </Button>
            </Box>
          }
        >
          <Box className={classes.editorWrapper}>
            {!editorReady ? (
              <Box
                height="320px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CircularProgress size={24} />
              </Box>
            ) : (
            <Editor
              height="320px"
              defaultLanguage="sql"
              value={sql}
              onChange={value => setSql(value ?? '')}
              theme="vs-dark"
              onMount={editor => {
                editor.addAction({
                  id: 'run-query',
                  label: 'Run Query',
                  keybindings: [2048 | 3], // Ctrl/Cmd + Enter
                  run: () => handleRunQueryRef.current(),
                });
              }}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                automaticLayout: true,
                tabSize: 2,
                padding: { top: 12 },
              }}
            />
            )}
          </Box>

          <Box className={classes.toolbar}>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleRunQuery}
              disabled={loading || !sql.trim()}
            >
              {loading ? '실행 중...' : 'Run Query'}
            </Button>

            <Box flex={1} />

            <Tooltip title="이 SQL로 새 파이프라인을 생성합니다">
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleCreatePipeline}
                disabled={!sql.trim()}
              >
                Pipeline 생성
              </Button>
            </Tooltip>
          </Box>
        </InfoCard>

        <CreatePipelineDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          initialSql={sql}
        />

        {error && (
          <Box mt={2}>
            <Box className={classes.errorBox}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Error
              </Typography>
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                {error}
              </Typography>
            </Box>
          </Box>
        )}

        {result && (
          <Box mt={2}>
            <InfoCard title="Query Result">
              <Box className={classes.resultInfo}>
                <Chip label={`${result.totalRows}건`} size="small" color="primary" />
                <Chip label={`${result.columns.length}개 컬럼`} size="small" variant="outlined" />
              </Box>

              {result.rows.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  결과가 없습니다.
                </Typography>
              ) : (
                <>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headerCell} style={{ width: 56, minWidth: 56 }}>
                          #
                        </TableCell>
                        {result.columns.map(col => (
                          <TableCell key={col} className={classes.headerCell}>
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            <Typography variant="caption" color="textSecondary">
                              {page * rowsPerPage + idx + 1}
                            </Typography>
                          </TableCell>
                          {result.columns.map(col => {
                            const val = row[col];
                            const display = formatCellValue(val);
                            const isNull = val === null || val === undefined;
                            return (
                              <TableCell key={col} className={classes.dataCell}>
                                <div className={isNull ? classes.nullValue : undefined}>
                                  {display}
                                </div>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={result.rows.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Rows:"
                />
                </>
              )}
            </InfoCard>
          </Box>
        )}
    </>
  );
};

export const SqlConsolePage = () => (
  <Page themeId="tool">
    <Header
      title="SQL Console"
      subtitle="BigQuery SQL 쿼리를 실행하고 결과를 확인하세요"
    />
    <Content>
      <SqlConsoleContent />
    </Content>
  </Page>
);
